/**
 * AgentSignaturePreviewCard - Cartão de configuração da assinatura do agente
 *
 * Mostra o switch de ativação junto de um balão de conversa de exemplo,
 * deixando claro na prática o efeito da opção: com a assinatura ligada,
 * o nome do agente aparece em destaque antes da mensagem enviada.
 *
 * RELEASE:
 *   2026-08-08 v1.0.0 - Versão inicial (extraído do CreateInboxDialog).
 */
import { Pen, User } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface AgentSignaturePreviewCardProps {
  /** Estado atual da assinatura */
  enabled: boolean;
  /** Callback de alteração do switch */
  onChange: (value: boolean) => void;
  /** Nome exibido no balão de exemplo (fallback: "João Silva") */
  agentName?: string | null;
  /** Desabilita o controle */
  disabled?: boolean;
}

export function AgentSignaturePreviewCard({
  enabled,
  onChange,
  agentName,
  disabled,
}: AgentSignaturePreviewCardProps) {
  // Nome usado apenas para ilustrar o preview
  const displayName = (agentName || '').trim() || 'João Silva';

  return (
    <div className="rounded-2xl border bg-muted/30 p-4 space-y-4">
      {/* Cabeçalho: ícone + título + switch */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Pen className="h-4 w-4 text-primary" />
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-foreground leading-tight">
              Assinatura do agente
            </h4>
            <p className="text-xs text-muted-foreground leading-snug">
              Adiciona o nome do agente no início de cada mensagem enviada.
            </p>
          </div>
        </div>
        <Switch
          id="signature-enabled-create"
          checked={enabled}
          onCheckedChange={onChange}
          disabled={disabled}
        />
      </div>

      {/* Balão de conversa de exemplo */}
      <div className="flex items-end gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <User className="h-4 w-4 text-primary" />
        </div>
        <div
          className={cn(
            'max-w-full rounded-2xl rounded-bl-sm border bg-card px-3 py-2 shadow-sm transition-all',
            enabled && 'border-primary/30',
          )}
        >
          {enabled && (
            <p className="text-xs font-semibold text-foreground">{displayName}</p>
          )}
          <p className="text-sm text-foreground">Olá! Como posso te ajudar hoje?</p>
          <p className="mt-0.5 text-right text-[10px] text-muted-foreground">10:30</p>
        </div>
      </div>
    </div>
  );
}
